import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { auth } from "@/lib/auth";
import { Mic2, Play, Youtube, Music4, Percent, Palette, Camera, Loader2, BadgeCheck, ExternalLink, Share2, Check, FileText, FileDown, Upload, Trash2, Sparkles } from "lucide-react";
import Creator6xo from "@/components/Creator6xo";
import PresskitCustomizer from "@/components/PresskitCustomizer";
import ShareMediaButton from "@/components/ShareMediaButton";

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
    violet: { label: "VIOLET", bg: "radial-gradient(60% 50% at 50% 0%, rgba(138,43,226,0.30) 0%, transparent 70%)", ring: "border-violet/60", text: "text-violet" },
    gold: { label: "OR", bg: "radial-gradient(60% 50% at 50% 0%, rgba(212,175,55,0.28) 0%, transparent 70%)", ring: "border-gold/60", text: "text-gold" },
    red: { label: "ROUGE", bg: "radial-gradient(60% 50% at 50% 0%, rgba(200,40,40,0.28) 0%, transparent 70%)", ring: "border-red-500/60", text: "text-red-400" },
    blue: { label: "BLEU", bg: "radial-gradient(60% 50% at 50% 0%, rgba(60,90,220,0.30) 0%, transparent 70%)", ring: "border-blue-500/60", text: "text-blue-400" },
    green: { label: "VERT", bg: "radial-gradient(60% 50% at 50% 0%, rgba(40,160,90,0.28) 0%, transparent 70%)", ring: "border-green-500/60", text: "text-green-400" },
};

const ytUrl = (id) => `https://youtu.be/${id}`;
const ytChannelSearch = (name) => `https://www.youtube.com/results?search_query=${encodeURIComponent(name)}`;
const spotifySearch = (name) => `https://open.spotify.com/search/${encodeURIComponent(name)}`;

function ClaimForm({ roster, onClaimed }) {
    const { lang } = useLang();
    const [form, setForm] = useState({ first_name: "", last_name: "", artist_name: "", instagram: "", youtube_link: "", answer: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const selected = roster.find((a) => a.artist.toLowerCase() === form.artist_name.trim().toLowerCase());
    const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet";

    const submit = async () => {
        if (!form.first_name.trim() || !form.last_name.trim() || !form.artist_name.trim() || !form.instagram.trim()) {
            setErr(tx(lang, T.artists.err));
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const r = await auth.fetch(`${API}/api/artists/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const d = await r.json();
            if (r.status === 403) { setErr(tx(lang, T.artists.wrongAnswer)); return; }
            if (r.status === 404) { setErr(tx(lang, T.artists.notRoster)); return; }
            if (!r.ok) throw new Error();
            onClaimed(d);
        } catch {
            setErr(tx(lang, T.artists.errServer));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="artist-claim-form" className="mx-auto max-w-md space-y-5">
            <div className="grid grid-cols-2 gap-4">
                <input data-testid="claim-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={tx(lang, T.artists.firstName)} className={inputCls} />
                <input data-testid="claim-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={tx(lang, T.artists.lastName)} className={inputCls} />
            </div>
            <select
                data-testid="claim-artist"
                value={form.artist_name}
                onChange={(e) => setForm({ ...form, artist_name: e.target.value, answer: "" })}
                className="w-full border-b border-white/15 bg-ink py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none focus:border-violet"
            >
                <option value="">{tx(lang, T.artists.pickArtist)}</option>
                {roster.map((a) => <option key={a.slug} value={a.artist} disabled={a.claimed}>{a.artist}{a.claimed ? " — ✓" : ""}</option>)}
            </select>
            <input data-testid="claim-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder={tx(lang, T.artists.instagram)} className={inputCls} />
            <input data-testid="claim-youtube" value={form.youtube_link} onChange={(e) => setForm({ ...form, youtube_link: e.target.value })} placeholder={tx(lang, T.artists.youtubeLink)} className={inputCls} />
            {selected?.has_question && (
                <div className="border border-gold/30 bg-gold/5 p-4">
                    <p className="font-mono text-[9px] tracking-[0.2em] text-gold">{selected.question_text}</p>
                    <div className="mt-3 flex gap-3">
                        {["portugal", "espagne"].map((a) => (
                            <button
                                key={a}
                                data-testid={`claim-answer-${a}`}
                                onClick={() => setForm({ ...form, answer: a })}
                                className={`flex-1 border px-4 py-2 font-mono text-[10px] tracking-[0.25em] transition-all ${form.answer === a ? "border-gold bg-gold text-ink" : "border-white/20 text-cream/60 hover:border-gold"}`}
                            >
                                {a.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {err && <p data-testid="claim-error" className="font-mono text-[10px] tracking-[0.2em] text-red-400">{err}</p>}
            <button
                data-testid="claim-submit"
                onClick={submit}
                disabled={busy}
                className="flex w-full items-center justify-center gap-3 border border-violet px-6 py-4 font-mono text-[10px] tracking-[0.35em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-50"
            >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Mic2 size={13} />}
                {tx(lang, T.artists.claimBtn)}
            </button>
        </div>
    );
}

function PressKitPanel({ slug, presskit, onUpdated }) {
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [draft, setDraft] = useState({
        style: presskit.style || "",
        city: presskit.city || "",
        color1: presskit.color1 || "violet",
        color2: presskit.color2 || "gold",
        bio: presskit.bio || "",
        youtube: (presskit.links || {}).youtube || "",
        spotify: (presskit.links || {}).spotify || "",
        instagram: (presskit.links || {}).instagram || "",
    });
    const [photos, setPhotos] = useState(presskit.photos || []);
    const [aiBusy, setAiBusy] = useState(false);
    const fileRef = useRef(null);
    const inputCls = "w-full border-b border-white/15 bg-transparent py-2 font-mono text-[10px] tracking-[0.12em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";

    // Claude rédige/améliore la bio à partir du style + ville + bio actuelle
    const aiBio = async () => {
        setAiBusy(true);
        try {
            const r = await auth.fetch(`${API}/api/artists/bio-assist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ slug, style: draft.style, city: draft.city, current_bio: draft.bio }),
            });
            if (!r.ok) throw new Error();
            const d = await r.json();
            if (d.bio) setDraft((prev) => ({ ...prev, bio: d.bio }));
        } catch { /* silencieux — le champ reste éditable */ }
        setAiBusy(false);
    };

    const save = async () => {
        setBusy(true);
        try {
            const r = await auth.fetch(`${API}/api/artists/${slug}/presskit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    style: draft.style,
                    city: draft.city,
                    color1: draft.color1,
                    color2: draft.color2,
                    bio: draft.bio,
                    links: { youtube: draft.youtube, spotify: draft.spotify, instagram: draft.instagram },
                }),
            });
            const d = await r.json();
            if (r.ok) {
                onUpdated({ ...d.presskit, photos });
                setSaved(true);
                setTimeout(() => setSaved(false), 2500);
            }
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        setBusy(true);
        try {
            const r = await auth.fetch(`${API}/api/artists/${slug}/presskit/photo`, { method: "POST", body: fd });
            const d = await r.json();
            if (r.ok) {
                setPhotos((p) => [...p, d.filename]);
                onUpdated({ ...presskit, photos: [...photos, d.filename] });
            }
        } finally {
            setBusy(false);
            e.target.value = "";
        }
    };

    const removePhoto = async (name) => {
        // suppression côté backend via photo/delete non implémentée → on retire localement et on ré-enregistre la liste
        const next = photos.filter((p) => p !== name);
        setPhotos(next);
        onUpdated({ ...presskit, photos: next });
        // persist : le backend n'a pas d'endpoint de suppression unitaire → on laisse la liste locale ; le presskit endpoint remplace `presskit` sans photos → on garde photos séparées
    };

    return (
        <div data-testid="presskit-panel" className="mt-8 border border-gold/25 bg-gold/5 p-6">
            <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-gold">
                    <FileText size={12} /> {lang === "fr" ? "PRESS KIT AUTOMATIQUE" : "AUTOMATIC PRESS KIT"}
                </p>
                <button
                    data-testid="presskit-toggle"
                    onClick={() => setOpen(!open)}
                    className="border border-gold/60 px-4 py-1.5 font-mono text-[8px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink"
                >
                    {open ? (lang === "fr" ? "FERMER" : "CLOSE") : (lang === "fr" ? "COMPLÉTER MON PRESS KIT" : "COMPLETE MY PRESS KIT")}
                </button>
            </div>
            <p className="mt-3 font-mono text-[8px] leading-relaxed tracking-[0.2em] text-cream/45">
                {lang === "fr"
                    ? "Ajoutez vos photos et infos — votre press kit public se génère automatiquement et peut être partagé aux médias et bookers."
                    : "Add your photos and info — your public press kit is generated automatically and can be shared with media and bookers."}
            </p>
            {open && (
                <div data-testid="presskit-form" className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <input data-testid="pk-style" value={draft.style} onChange={(e) => setDraft({ ...draft, style: e.target.value })} placeholder={lang === "fr" ? "STYLE MUSICAL (ex : drill mélancolique, rap conscient…)" : "MUSICAL STYLE (e.g. melodic drill, conscious rap…)"} className={inputCls} />
                        <input data-testid="pk-city" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder={lang === "fr" ? "VILLE / SCÈNE" : "CITY / SCENE"} className={inputCls} />
                    </div>
                    <div>
                        <div className="mb-1 flex items-center justify-between">
                            <span className="font-mono text-[8px] tracking-[0.25em] text-cream/40">BIO</span>
                            <button
                                data-testid="pk-ai-bio"
                                onClick={aiBio}
                                disabled={aiBusy}
                                className="flex items-center gap-1.5 border border-violet/60 px-3 py-1.5 font-mono text-[8px] tracking-[0.2em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-50"
                            >
                                {aiBusy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                                {lang === "fr" ? "RÉDIGER AVEC CLAUDE" : "WRITE WITH CLAUDE"}
                            </button>
                        </div>
                        <textarea
                            data-testid="pk-bio"
                            value={draft.bio}
                            onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
                            placeholder={lang === "fr" ? "BIO COURTE POUR LES MÉDIAS (600 caractères max)…" : "SHORT MEDIA BIO (600 chars max)…"}
                            rows={3}
                            className={`${inputCls} resize-none`}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <input data-testid="pk-link-youtube" value={draft.youtube} onChange={(e) => setDraft({ ...draft, youtube: e.target.value })} placeholder="YOUTUBE (https://…)" className={inputCls} />
                        <input data-testid="pk-link-spotify" value={draft.spotify} onChange={(e) => setDraft({ ...draft, spotify: e.target.value })} placeholder="SPOTIFY (https://…)" className={inputCls} />
                        <input data-testid="pk-link-instagram" value={draft.instagram} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} placeholder="INSTAGRAM (https://…)" className={inputCls} />
                    </div>
                    {/* couleurs du press kit */}
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[8px] tracking-[0.2em] text-cream/50">{lang === "fr" ? "COULEUR 1" : "COLOR 1"}</span>
                            {Object.entries(COLORS).map(([k, v]) => (
                                <button key={k} data-testid={`pk-c1-${k}`} onClick={() => setDraft({ ...draft, color1: k })}
                                    className={`h-7 w-7 rounded-full border-2 transition-all ${draft.color1 === k ? "scale-110 border-cream" : "border-white/20"}`}
                                    style={{ background: { violet: "#8a2be2", gold: "#d4af37", red: "#c82828", blue: "#3c5adc", green: "#28a05a" }[k] }} aria-label={v.label} />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[8px] tracking-[0.2em] text-cream/50">{lang === "fr" ? "COULEUR 2" : "COLOR 2"}</span>
                            {Object.entries(COLORS).map(([k, v]) => (
                                <button key={k} data-testid={`pk-c2-${k}`} onClick={() => setDraft({ ...draft, color2: k })}
                                    className={`h-7 w-7 rounded-full border-2 transition-all ${draft.color2 === k ? "scale-110 border-cream" : "border-white/20"}`}
                                    style={{ background: { violet: "#8a2be2", gold: "#d4af37", red: "#c82828", blue: "#3c5adc", green: "#28a05a" }[k] }} aria-label={v.label} />
                            ))}
                        </div>
                    </div>
                    {/* photos */}
                    <div>
                        <div className="flex items-center justify-between">
                            <p className="font-mono text-[8px] tracking-[0.2em] text-cream/50">{lang === "fr" ? "PHOTOS PRESSE (HD)" : "PRESS PHOTOS (HD)"} — {photos.length}</p>
                            <button data-testid="pk-add-photo" onClick={() => fileRef.current?.click()} disabled={busy}
                                className="flex items-center gap-2 border border-gold/60 px-4 py-1.5 font-mono text-[8px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40">
                                <Upload size={11} /> {lang === "fr" ? "AJOUTER" : "ADD"}
                            </button>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                        </div>
                        {photos.length > 0 && (
                            <div data-testid="pk-photos" className="mt-4 grid grid-cols-3 gap-3 md:grid-cols-5">
                                {photos.map((name) => (
                                    <div key={name} className="group relative aspect-square overflow-hidden border border-white/10">
                                        <img src={`${API}/api/uploads/offers/${name}`} alt="" className="h-full w-full object-cover" />
                                        <button data-testid={`pk-del-${name}`} onClick={() => removePhoto(name)}
                                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-ink/80 text-red-400 opacity-0 transition-opacity group-hover:opacity-100">
                                            <Trash2 size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        data-testid="pk-save"
                        onClick={save}
                        disabled={busy}
                        className="flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-3 font-mono text-[9px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                        {lang === "fr" ? "ENREGISTRER MON PRESS KIT" : "SAVE MY PRESS KIT"}
                    </button>
                    {saved && <p data-testid="pk-saved" className="font-mono text-[9px] tracking-[0.25em] text-green-400">{lang === "fr" ? "PRESS KIT ENREGISTRÉ" : "PRESS KIT SAVED"}</p>}
                    {/* personnalisation du PDF avant téléchargement */}
                    <PresskitCustomizer slug={slug} lang={lang} />
                    <button
                        data-testid="pk-view-public"
                        onClick={() => { window.__presskitSlug = slug; window.__goPage?.("presskit"); }}
                        className="flex w-full items-center justify-center gap-2 border border-white/20 py-3 font-mono text-[9px] tracking-[0.3em] text-cream/70 transition-all hover:border-gold hover:text-gold"
                    >
                        <ExternalLink size={12} /> {lang === "fr" ? "VOIR MON PRESS KIT PUBLIC" : "VIEW MY PUBLIC PRESS KIT"}
                    </button>
                </div>
            )}
        </div>
    );
}

function ShareButton({ slug, lang }) {
    const [copied, setCopied] = useState(false);
    const share = async () => {
        const url = `${window.location.origin}/#artiste?artist=${slug}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "GLOOM ENT.", url });
                return;
            }
            throw new Error("no native share");
        } catch {
            try {
                await navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch { /* clipboard blocked */ }
        }
    };
    return (
        <button
            data-testid="artist-share-btn"
            onClick={share}
            className="mt-3 inline-flex items-center gap-2 border border-white/20 px-4 py-1.5 font-mono text-[9px] tracking-[0.25em] text-cream/60 transition-all hover:border-violet hover:text-violet"
        >
            {copied ? <Check size={12} /> : <Share2 size={12} />}
            {copied ? (lang === "fr" ? "LIEN COPIÉ" : "LINK COPIED") : (lang === "fr" ? "PARTAGER MA PAGE" : "SHARE MY PAGE")}
        </button>
    );
}

function ArtistView({ profile, mine, onUpdated }) {
    // presskit est chargé avec le profil complet (owner) ou reste {} pour le roster
    const { lang } = useLang();
    const [videoOpen, setVideoOpen] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const fileRef = useRef(null);
    const c = COLORS[profile.color] || COLORS.violet;
    const name = profile.artist;
    const links = profile.links || {};
    const ytLink = links.youtube || ytChannelSearch(name);
    const spLink = links.spotify || spotifySearch(name);
    const igHandle = (profile.instagram || "").replace("@", "");
    const igLink = links.instagram || (igHandle ? `https://instagram.com/${igHandle}` : null);

    const startEdit = () => {
        setDraft({ bio: profile.bio || "", youtube: links.youtube || "", spotify: links.spotify || "", instagram: links.instagram || "" });
        setEditing(true);
    };

    const saveEdit = async () => {
        setBusy(true);
        try {
            const body = {
                color: profile.color,
                bio: draft.bio,
                links: { youtube: draft.youtube, spotify: draft.spotify, instagram: draft.instagram },
            };
            await auth.fetch(`${API}/api/artists/${profile.slug}/customize`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            onUpdated({ ...profile, bio: body.bio, links: body.links });
            setEditing(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } finally {
            setBusy(false);
        }
    };

    const setColor = async (color) => {
        setBusy(true);
        try {
            await auth.fetch(`${API}/api/artists/${profile.slug}/customize`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ color }),
            });
            onUpdated({ ...profile, color });
        } finally {
            setBusy(false);
        }
    };

    const setPhoto = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const rd = new FileReader();
        rd.onload = async () => {
            const photo = rd.result;
            await auth.fetch(`${API}/api/artists/${profile.slug}/customize`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ color: profile.color, photo }),
            });
            onUpdated({ ...profile, photo });
        };
        rd.readAsDataURL(f);
    };

    const inputCls = "w-full border-b border-white/15 bg-transparent py-2 font-mono text-[10px] tracking-[0.12em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";

    return (
        <div className="relative overflow-hidden">
            {/* cover band */}
            <div className="pointer-events-none absolute inset-0" style={{ background: c.bg }} />
            <div className="relative">
                {/* header animé — lettres en cascade */}
                <FadeUp delay={0.05}>
                    <div className="flex flex-col items-center pt-6 text-center">
                        <div className={`relative h-32 w-32 overflow-hidden rounded-full border-2 ${c.ring} shadow-[0_20px_60px_rgba(0,0,0,0.5)]`}>
                            {profile.photo ? (
                                <img data-testid="artist-photo" src={profile.photo} alt={name} className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center bg-white/5">
                                    <Mic2 size={34} className={c.text} />
                                </div>
                            )}
                            {mine && (
                                <button data-testid="artist-photo-btn" onClick={() => fileRef.current?.click()} className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-ink/80 text-cream/80 hover:text-cream" aria-label="photo">
                                    <Camera size={13} />
                                </button>
                            )}
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={setPhoto} />
                        </div>
                        <h2 data-testid="artist-name" className="mt-6 flex flex-wrap justify-center font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl">
                            {name.split("").map((ch, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, y: 24, rotateX: 90 }}
                                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                                    transition={{ delay: 0.15 + i * 0.045, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {ch === " " ? "\u00A0" : ch}
                                </motion.span>
                            ))}
                        </h2>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.7 }}
                            className="mt-3 inline-flex items-center gap-2 border border-gold/50 bg-gold/10 px-4 py-1.5 font-mono text-[9px] tracking-[0.25em] text-gold"
                        >
                            <BadgeCheck size={12} /> {tx(lang, T.artists.gloomArtist)}
                        </motion.div>
                        <ShareButton slug={profile.slug} lang={lang} />
                        <a
                            data-testid="artist-presskit-pdf"
                            href={`${API}/api/artists/${profile.slug}/presskit.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-2 border border-violet/70 bg-violet/10 px-4 py-1.5 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                        >
                            <FileDown size={12} /> {lang === "fr" ? "DOSSIER PRESSE PDF" : "PRESS KIT PDF"}
                        </a>
                    </div>
                </FadeUp>

                {/* bio */}
                {profile.bio && (
                    <FadeUp delay={0.15}>
                        <p data-testid="artist-bio" className="mx-auto mt-6 max-w-xl text-center font-serif text-sm italic leading-relaxed text-cream/70">
                            {profile.bio}
                        </p>
                    </FadeUp>
                )}

                {/* rabais exclusif */}
                <FadeUp delay={0.18}>
                    <div data-testid="artist-discount" className="mx-auto mt-8 flex max-w-lg items-center justify-center gap-3 border border-gold/40 bg-gold/10 px-6 py-4 text-center">
                        <Percent size={15} className="shrink-0 text-gold" />
                        <p className="font-mono text-[10px] leading-relaxed tracking-[0.2em] text-gold">
                            {tx(lang, T.artists.discount)}
                        </p>
                    </div>
                </FadeUp>

                {/* réseaux — chaîne YouTube, Spotify, Instagram */}
                <FadeUp delay={0.22}>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <a data-testid="artist-youtube-link" href={ytLink} target="_blank" rel="noreferrer"
                            className="group flex items-center gap-2 border border-red-500/50 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-red-400 transition-all hover:bg-red-500 hover:text-cream">
                            <Youtube size={14} className="transition-transform group-hover:scale-110" /> YOUTUBE
                        </a>
                        <a data-testid="artist-spotify-link" href={spLink} target="_blank" rel="noreferrer"
                            className="group flex items-center gap-2 border border-green-500/50 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-green-400 transition-all hover:bg-green-500 hover:text-cream">
                            <Music4 size={14} className="transition-transform group-hover:scale-110" /> SPOTIFY
                        </a>
                        {igLink && (
                            <a data-testid="artist-instagram-link" href={igLink} target="_blank" rel="noreferrer"
                                className="group flex items-center gap-2 border border-fuchsia-500/50 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-fuchsia-400 transition-all hover:bg-fuchsia-500 hover:text-cream">
                                <ExternalLink size={14} className="transition-transform group-hover:scale-110" /> INSTAGRAM{igHandle ? ` — @${igHandle}` : ""}
                            </a>
                        )}
                    </div>
                </FadeUp>

                {/* clips du book — numérotés, play pulsant */}
                <FadeUp delay={0.26}>
                    {(profile.clips || []).length > 0 && (
                        <p className="mt-12 text-center font-mono text-[9px] tracking-[0.35em] text-cream/40">
                            {lang === "fr" ? "CLIPS DU BOOK" : "BOOK CLIPS"} — {(profile.clips || []).length}
                        </p>
                    )}
                    <div className="mt-6 grid gap-6 md:grid-cols-2">
                        {(profile.clips || []).map((clip, ci) => (
                            <motion.div
                                key={clip.id}
                                data-testid={`artist-clip-${clip.id}`}
                                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: ci * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ y: -4 }}
                                className="border border-white/10 bg-ink/60 transition-colors duration-300 hover:border-violet/50"
                            >
                                {videoOpen === clip.id ? (
                                    <div className="aspect-video">
                                        <iframe src={`https://www.youtube.com/embed/${clip.id}?autoplay=1&rel=0`} title={clip.title} allow="autoplay; encrypted-media" allowFullScreen className="h-full w-full" />
                                    </div>
                                ) : (
                                    <button onClick={() => setVideoOpen(clip.id)} className="group relative block aspect-video w-full overflow-hidden">
                                        <img src={`https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`} alt={clip.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        <span className="absolute inset-0 flex items-center justify-center bg-ink/40 transition-colors duration-300 group-hover:bg-ink/25">
                                            <span className="relative flex h-14 w-14 items-center justify-center rounded-full border border-cream/60 bg-ink/60 transition-transform duration-300 group-hover:scale-110">
                                                {/* anneau pulsant façon REC */}
                                                <span className="absolute inset-0 animate-ping rounded-full border border-blood/60" />
                                                <Play size={20} className="ml-1 text-cream" />
                                            </span>
                                        </span>
                                    </button>
                                )}
                                <div className="flex items-center justify-between p-4">
                                    <p className="font-mono text-[10px] tracking-[0.2em] text-cream/70">
                                        <span className="mr-2 text-violet">{String(ci + 1).padStart(2, "0")}</span>
                                        {clip.title}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <ShareMediaButton
                                            kind="video"
                                            src={`https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`}
                                            clip={ytUrl(clip.id)}
                                            title={`${profile.artist} — ${clip.title}`}
                                            target={`#artiste?artist=${profile.slug}`}
                                            testid={`share-clip-${clip.id}`}
                                        />
                                        <a href={ytUrl(clip.id)} target="_blank" rel="noreferrer" className="text-cream/40 transition-colors hover:text-cream">
                                            <ExternalLink size={13} />
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </FadeUp>

                {/* espace privé — édition du book */}
                {mine && (
                    <FadeUp delay={0.3}>
                        <div className="mx-auto mt-10 max-w-xl border border-violet/30 bg-violet/5 p-6">
                            <div className="flex items-center justify-between">
                                <p className="font-mono text-[9px] tracking-[0.3em] text-violet">
                                    {lang === "fr" ? "ESPACE PRIVÉ — VOTRE BOOK" : "PRIVATE — YOUR BOOK"}
                                </p>
                                <button
                                    data-testid="artist-edit-btn"
                                    onClick={() => (editing ? setEditing(false) : startEdit())}
                                    className="border border-violet/60 px-4 py-1.5 font-mono text-[8px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream"
                                >
                                    {editing ? (lang === "fr" ? "FERMER" : "CLOSE") : (lang === "fr" ? "MODIFIER" : "EDIT")}
                                </button>
                            </div>
                            {/* couleur de fond */}
                            <div className="mt-5 flex items-center gap-3">
                                <Palette size={13} className="text-cream/50" />
                                {Object.entries(COLORS).map(([k, v]) => (
                                    <button
                                        key={k}
                                        data-testid={`color-${k}`}
                                        onClick={() => setColor(k)}
                                        disabled={busy}
                                        className={`h-8 w-8 rounded-full border-2 transition-all ${profile.color === k ? "scale-110 border-cream" : "border-white/20 hover:scale-105"}`}
                                        style={{ background: { violet: "#8a2be2", gold: "#d4af37", red: "#c82828", blue: "#3c5adc", green: "#28a05a" }[k] }}
                                        aria-label={v.label}
                                    />
                                ))}
                            </div>
                            {editing && draft && (
                                <div data-testid="artist-edit-panel" className="mt-6 space-y-4">
                                    <textarea
                                        data-testid="edit-bio"
                                        value={draft.bio}
                                        onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
                                        placeholder={lang === "fr" ? "VOTRE BIO (600 caractères max)…" : "YOUR BIO (600 chars max)…"}
                                        rows={3}
                                        className={`${inputCls} resize-none`}
                                    />
                                    <input data-testid="edit-link-youtube" value={draft.youtube} onChange={(e) => setDraft({ ...draft, youtube: e.target.value })} placeholder="LIEN CHAÎNE YOUTUBE (https://…)" className={inputCls} />
                                    <input data-testid="edit-link-spotify" value={draft.spotify} onChange={(e) => setDraft({ ...draft, spotify: e.target.value })} placeholder="LIEN SPOTIFY (https://…)" className={inputCls} />
                                    <input data-testid="edit-link-instagram" value={draft.instagram} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} placeholder="LIEN INSTAGRAM (https://…)" className={inputCls} />
                                    <button
                                        data-testid="edit-save"
                                        onClick={saveEdit}
                                        disabled={busy}
                                        className="flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-3 font-mono text-[9px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
                                    >
                                        {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                                        {lang === "fr" ? "ENREGISTRER" : "SAVE"}
                                    </button>
                                </div>
                            )}
                            {saved && <p data-testid="edit-saved" className="mt-3 font-mono text-[9px] tracking-[0.25em] text-green-400">{lang === "fr" ? "ENREGISTRÉ" : "SAVED"}</p>}
                        </div>
                        <PressKitPanel
                            slug={profile.slug}
                            presskit={profile.presskit || {}}
                            onUpdated={(pk) => onUpdated({ ...profile, presskit: pk })}
                        />
                    </FadeUp>
                )}
            </div>
        </div>
    );
}

export default function ArtistPage() {
    const { lang } = useLang();
    const [roster, setRoster] = useState([]);
    const [claimed, setClaimed] = useState(null);
    const [view, setView] = useState(null); // slug affiché
    const [user, setUser] = useState(null);

    useEffect(() => auth.subscribe((s) => setUser(s.user)), []);
    // lien direct : #artiste?artist=<slug> ouvre directement le profil
    useEffect(() => {
        const q = new URLSearchParams(window.location.hash.split("?")[1] || "");
        const slug = q.get("artist");
        if (slug) setView(slug);
    }, []);
    useEffect(() => {
        fetch(`${API}/api/artists`).then((r) => r.json()).then((d) => setRoster(d.artists || [])).catch(() => {});
    }, [claimed]);
    // deep-link après claim depuis la page de connexion
    useEffect(() => {
        const pending = window.__artistClaim;
        if (pending) {
            window.__artistClaim = null;
            setClaimed(pending);
            setView(pending.slug);
        }
    }, []);

    const mySlug = claimed?.slug || roster.find((a) => a.claimed && user && a.owner_user === user.user_id)?.slug;
    const displayed = view ? roster.find((a) => a.slug === view) : null;
    const mineProfile = claimed || (mySlug ? roster.find((a) => a.slug === mySlug) : null);

    return (
        <section data-testid="artist-page" className="relative min-h-screen overflow-hidden bg-ink pb-24 text-cream">
            {/* bandeau défilant — identité label */}
            <div className="relative mt-[72px] overflow-hidden border-y border-white/10 bg-ink/80 py-2.5 backdrop-blur-md">
                <div className="animate-marquee flex w-max whitespace-nowrap">
                    {[0, 1].map((k) => (
                        <span key={k} className="flex items-center font-mono text-[10px] tracking-[0.4em] text-gold/80">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <span key={i} className="flex items-center">
                                    <span className="mx-6">GLOOM ARTISTS</span>
                                    <span className="text-violet">•</span>
                                    <span className="mx-6">{lang === "fr" ? "BOOKS OFFICIELS" : "OFFICIAL BOOKS"}</span>
                                    <span className="text-violet">•</span>
                                    <span className="mx-6">GENÈVE</span>
                                    <span className="text-violet">•</span>
                                </span>
                            ))}
                        </span>
                    ))}
                </div>
            </div>
            <div className="relative mx-auto max-w-[1100px] px-5 pt-16 md:px-10">
                <SectionLabel en={tx(lang, T.artists.label)} fr={tx(lang, T.artists.label)} />
                <FadeUp delay={0.05}>
                    <h1 className="mt-8 font-display text-[clamp(2.2rem,7vw,5.5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                        {tx(lang, T.artists.title)}<span className="text-violet">.</span>
                    </h1>
                    <p className="mt-4 max-w-xl font-mono text-[10px] leading-relaxed tracking-[0.25em] text-cream/45">
                        {tx(lang, T.artists.sub)}
                    </p>
                </FadeUp>

                {/* mon profil si réclamé */}
                {mineProfile && (
                    <FadeUp delay={0.1} className="mt-14">
                        <p className="font-mono text-[10px] tracking-[0.35em] text-gold">{tx(lang, T.artists.myProfile)}</p>
                        <div className="mt-6 border border-white/10 p-8 md:p-12">
                            <ArtistView profile={{ ...mineProfile, instagram: mineProfile.instagram || claimed?.instagram }} mine={!!claimed} onUpdated={setClaimed} />
                        </div>
                    </FadeUp>
                )}

                {/* profil affiché (carte cliquée) */}
                {displayed && !mineProfile && (
                    <FadeUp delay={0.1} className="mt-14">
                        <div className="border border-white/10 p-8 md:p-12">
                            <ArtistView profile={displayed} mine={false} onUpdated={() => {}} />
                        </div>
                    </FadeUp>
                )}

                {/* roster — liste éditoriale numérotée avec révélation de miniature */}
                <FadeUp delay={0.15} className="mt-16">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-violet">{tx(lang, T.artists.roster)}</p>
                    <div className="mt-6 space-y-2">
                        {roster.map((a, i) => {
                            const activeR = view === a.slug;
                            return (
                                <motion.button
                                    key={a.slug}
                                    data-testid={`roster-${a.slug}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    onClick={() => setView(activeR ? null : a.slug)}
                                    className={`group relative flex w-full items-center gap-4 overflow-hidden border p-4 text-left transition-all duration-300 sm:p-5 ${
                                        activeR
                                            ? "border-violet/60 bg-violet/[0.08]"
                                            : "border-white/5 bg-white/[0.02] hover:border-violet/50 hover:bg-white/[0.05]"
                                    }`}
                                >
                                    {/* numéro éditorial */}
                                    <span className={`w-12 shrink-0 font-display text-2xl font-extrabold transition-colors duration-300 sm:text-3xl ${activeR ? "text-violet" : "text-white/20 group-hover:text-violet"}`}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {/* identité */}
                                    <span className="min-w-0 flex-1">
                                        <span className={`block truncate font-sans text-base font-semibold uppercase tracking-tight transition-colors sm:text-lg ${activeR ? "text-cream" : "text-cream/90 group-hover:text-white"}`}>
                                            {a.artist}
                                        </span>
                                        <span className="mt-0.5 block truncate font-mono text-[9px] tracking-[0.2em] text-cream/40">
                                            {a.claimed ? tx(lang, T.artists.claimedTag) : `${(a.clips || []).length} CLIP${(a.clips || []).length > 1 ? "S" : ""}`}
                                        </span>
                                    </span>
                                    {/* pastille */}
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-cream/60 transition-all duration-300 group-hover:border-violet group-hover:bg-violet group-hover:text-cream">
                                        <Mic2 size={15} />
                                    </span>
                                    {/* miniature révélée au hover (desktop) */}
                                    <span className="pointer-events-none absolute right-24 top-1/2 hidden w-24 -translate-y-1/2 rotate-3 opacity-0 shadow-[0_15px_40px_rgba(0,0,0,0.6)] transition-all duration-300 group-hover:rotate-0 group-hover:opacity-100 md:block">
                                        <img src={`/img/artist-${a.slug}.jpg`} alt="" className="aspect-square w-full rounded-full border-2 border-white/20 object-cover" loading="lazy" />
                                    </span>
                                    {/* liseré violet */}
                                    <span className={`absolute left-0 top-0 h-full w-[3px] bg-violet transition-transform duration-300 ${activeR ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"}`} style={{ transformOrigin: "top" }} />
                                </motion.button>
                            );
                        })}
                    </div>
                </FadeUp>

                {/* espace créateur — 6xo */}
                <FadeUp delay={0.22} className="mt-20 border-t border-white/10 pt-14">
                    <p className="font-mono text-[10px] tracking-[0.35em] text-gold">{lang === "fr" ? "CRÉATEURS" : "CREATORS"}</p>
                    <div className="mt-6">
                        <Creator6xo />
                    </div>
                </FadeUp>

                {/* claim */}
                {!mineProfile && (
                    <FadeUp delay={0.2} className="mt-16 border-t border-white/10 pt-12">
                        <p className="text-center font-mono text-[10px] tracking-[0.35em] text-gold">{tx(lang, T.artists.claimTitle)}</p>
                        <p className="mx-auto mt-3 max-w-md text-center font-mono text-[9px] leading-relaxed tracking-[0.25em] text-cream/45">{tx(lang, T.artists.claimSub)}</p>
                        <div className="mt-8">
                            <ClaimForm roster={roster} onClaimed={setClaimed} />
                        </div>
                    </FadeUp>
                )}
            </div>
        </section>
    );
}
